import { format } from 'date-fns';
import { BASE_URL } from '../configs/config';

export const JST_TIME_ZONE = 'Asia/Tokyo';
let serverOffsetMs: number | null = null;
let syncPromise: Promise<boolean> | null = null;

const getAdjustedNow = () => new Date(Date.now() + (serverOffsetMs ?? 0));

export const syncServerClock = async () => {
  if (serverOffsetMs !== null) return true;
  if (syncPromise) return syncPromise;

  syncPromise = (async () => {
    try {
      const response = await fetch(`${BASE_URL}serverTime`, { cache: 'no-store' });
      if (!response.ok) return false;

      const data = await response.json();
      if (!Number.isFinite(data?.serverTime)) return false;

      serverOffsetMs = data.serverTime - Date.now();
      return true;
    } catch {
      return false;
    } finally {
      syncPromise = null;
    }
  })();

  return syncPromise;
};

const getDateParts = (date: Date) => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: JST_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    hourCycle: 'h23',
  });

  return Object.fromEntries(formatter.formatToParts(date).map((part) => [part.type, part.value]));
};

export const getCurrentJstDate = () => {
  const parts = getDateParts(getAdjustedNow());
  return new Date(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
    0
  );
};

export const getJstDateKey = (date?: Date | string | null) => {
  if (!date) return format(getCurrentJstDate(), 'yyyy-MM-dd');
  const target = typeof date === 'string' ? new Date(date) : date;
  return format(target, 'yyyy-MM-dd');
};

export const parseJstDateTime = (date: string, hours: number = 0, minutes: number = 0) => {
  const [year, month, day] = getJstDateKey(date).split('-').map(Number);
  return new Date(year, month - 1, day, hours, minutes, 0, 0);
};

export const isSameJstDate = (date: Date | string, compareDate: Date = getCurrentJstDate()) => {
  return getJstDateKey(date) === getJstDateKey(compareDate);
};
